<template>
  <form-dialog
    ref="formDialog"
    v-model="dialogOpen"
    :title="isEditMode ? $t('Edit Material Expense') : $t('Materials')"
    @submit="submitForm"
    @close="onClose"
  >
    <template v-if="!isEditMode" #activator="{ on }">
      <div class="d-flex align-center">
        <v-btn :disabled="disabled" outlined small color="info" v-on="on">
          <v-icon x-small left>fas fa-plus</v-icon> {{ $t('Materials') }}
        </v-btn>
        <v-tooltip v-if="showPayrollHint" bottom>
          <template #activator="{ on: tooltipOn, attrs }">
            <v-icon
              small
              color="grey"
              class="ml-1"
              v-bind="attrs"
              v-on="tooltipOn"
            >
              mdi-information-outline
            </v-icon>
          </template>
          <span>{{
            $t(
              'Material expenses are not included in payroll contractor payments'
            )
          }}</span>
        </v-tooltip>
      </div>
    </template>
    <v-select
      v-model="expenseCategory"
      :label="$t('Expense Category')"
      outlined
      dense
      :items="['Repair Materials', 'Supplies', 'Subcontracted labor']"
    />
    <expenses-types-autocomplete
      v-model="expenseType"
      :label="$t('Expense Type')"
      outlined
      clearable
    />
    <v-text-field
      v-model.number="amount"
      :label="$t('Amount')"
      :rules="[required, isPositive]"
      dense
      outlined
      type="number"
      step="0.01"
      min="0"
    />
    <v-textarea
      v-model="description"
      :label="$t('Description')"
      :rules="[required]"
      dense
      outlined
    />
    <v-autocomplete
      v-if="isDesignedVr"
      v-model="bankAccountId"
      :label="$t('Card owner')"
      dense
      outlined
      clearable
      :allow-overflow="false"
      :items="accountNumbers"
    />
    <date-picker
      v-model="paidAt"
      :label="$t('paid at')"
      :hide-details="false"
      :rules="[required]"
    />
    <dvr-media-upload
      :label="$t('Invoice')"
      :value.sync="images"
      folder="service-calls"
    />
  </form-dialog>
</template>

<script>
import FormDialog from 'components/common/form-dialog'
import FormRules from 'components/mixins/form-rules-mixin'
import DvrMediaUpload from 'components/common/dvr-media-upload'
import DatePicker from 'components/form-fields/date-picker'
import ExpensesTypesAutocomplete from 'components/expenses-types-autocomplete'
export default {
  name: 'AddRepairMaterials',
  components: {
    DatePicker,
    DvrMediaUpload,
    FormDialog,
    ExpensesTypesAutocomplete,
  },
  mixins: [FormRules],
  props: ['taskId', 'disabled', 'expense', 'value', 'showPayrollHint'],
  data() {
    return {
      dialogOpen: false,
      amount: null,
      description: null,
      images: [],
      bankAccountId: null,
      expenseCategory: 'Repair Materials',
      expenseType: null,
      paidAt: this.$moment().format('YYYY-MM-DD'),
    }
  },
  computed: {
    isEditMode() {
      return !!this.expense
    },
    accountNumbers() {
      return this.$store.state.app.bankAccounts.map(ba => ({
        value: ba.id,
        text: `${
          ba.first_name || ba.last_name
            ? `${ba.first_name} ${ba.last_name}`
            : 'Company'
        } (${ba.account_number})`,
      }))
    },
    appSettings() {
      return this.$store.state.settings || {}
    },
  },
  watch: {
    value: {
      handler(val) {
        this.dialogOpen = val
      },
      immediate: true,
    },
    dialogOpen(val) {
      this.$emit('input', val)
    },
    expenseCategory(newCategory) {
      // Auto-populate expense type based on category selection (only in add mode)
      if (!newCategory || this.isEditMode) {
        return
      }

      const customSettings = this.appSettings.custom || {}
      if (newCategory === 'Repair Materials') {
        this.expenseType = customSettings.repair_materials_expense_type || null
      } else if (newCategory === 'Supplies') {
        this.expenseType = customSettings.supplies_expense_type || null
      }
    },
  },
  mounted() {
    if (this.expense) {
      // Pre-fill form for edit mode
      this.amount = this.expense.amount
      this.description = this.expense.description
      this.images = this.expense.images || []
      this.bankAccountId = this.expense.bank_account_id
      this.paidAt = this.expense.paid_at

      // Determine category and expense type from short_reason
      const shortReason = this.expense.short_reason
      if (
        shortReason === 'Repair Materials' ||
        shortReason === 'Supplies' ||
        shortReason === 'Subcontracted labor'
      ) {
        this.expenseCategory = shortReason
        this.expenseType = null
      } else {
        // short_reason contains the specific expense type
        this.expenseCategory = 'Repair Materials' // default category
        this.expenseType = shortReason
      }

      // Open dialog in edit mode
      this.dialogOpen = true
      // Explicitly open the form dialog after next tick
      this.$nextTick(() => {
        if (this.$refs.formDialog) {
          this.$refs.formDialog.open()
        }
      })
    } else {
      // Initialize expense type based on default category
      const customSettings = this.appSettings.custom || {}
      this.expenseType = customSettings.repair_materials_expense_type || null
    }
  },
  methods: {
    submitForm() {
      if (this.isEditMode) {
        this.$store.dispatch('updateRepairMaterials', {
          id: this.taskId,
          expenseId: this.expense.id,
          amount: this.amount,
          expense_type: this.expenseType || this.expenseCategory,
          description: this.description,
          bank_account_id: this.bankAccountId,
          images: this.images,
          paid_at: this.paidAt,
        })
      } else {
        this.$store.dispatch('addRepairMaterials', {
          id: this.taskId,
          amount: this.amount,
          expense_type: this.expenseType || this.expenseCategory,
          description: this.description,
          bank_account_id: this.bankAccountId,
          images: this.images,
          paid_at: this.paidAt,
        })
      }
    },
    onClose() {
      this.amount = null
      this.description = null
      this.images = []
      this.expenseCategory = 'Repair Materials'
      this.expenseType = null
      this.paidAt = this.$moment().format('YYYY-MM-DD')
      this.$emit('close')
    },
  },
}
</script>

<style scoped></style>
